import * as async from 'async';
import * as _ from 'underscore';
import { ContactsService } from '../shared/services/contacts.service';
import { Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UserDto } from '../shared/interfaces/users';
import { UsersService } from '../shared/services/users.service';
import { GroupsGlobalDto, MailRecipientDetailsDto } from '../shared/interfaces';
import { ContactsGlobalService } from '../shared/services/contacts-global.service';
import { RecipientsService } from '../shared/services/recipients.service';
import { MailDestinataire } from '../shared/models';

export interface GetMailRecipientsUtilsResponse {
  error?: string;
  userList?: MailDestinataire[];
  serviceList?: MailDestinataire[];
  freeList?: MailDestinataire[];
  recipientsFromQuery?: MailRecipientDetailsDto[];
  groupList?: MailDestinataire[];
}

export class GetMailRecipientsUtils {
  /**
   * Retrieves mail recipients based on the provided mail ID and categorizes them into users, services, and free recipients.
   *
   * @param mailId - The ID of the mail for which recipients need to be fetched.
   * @param servicesList - A list of available services to match with recipients.
   * @param recipientsService - Service to fetch mail recipients.
   * @param contactsService - Service to fetch contact information.
   * @param contactsGlobalService - Service to fetch global contact infos like their types,...
   * @param listOfGroups - A list of available groups to match with recipients.
   * @param usersService - Optional service to fetch user details. Only used if fetchAllInfos is true
   * @param fetchAllInfos - Optional flag indicating whether to fetch additional information for users.
   * @returns An observable containing categorized lists of mail recipients and any errors encountered.
   */

  public static getMailRecipients(
    mailId: number,
    servicesList: {
      label: string;
      value: number;
      parentId: number;
      name: string;
      key: string;
      selectable: boolean;
      children: [];
      description: string;
      email: string;
    }[],
    recipientsService: RecipientsService,
    contactsService: ContactsService,
    contactsGlobalService: ContactsGlobalService,
    listOfGroups: GroupsGlobalDto[],
    usersService?: UsersService,
    fetchAllInfos = false
  ): Observable<GetMailRecipientsUtilsResponse> {
    const recipientNotFreeList = [];
    const listOfFreeRecipients = [];
    const listOfUsersRecipients = [];
    const listOfServicesRecipients = [];
    const listOfGroupsRecipients = [];
    const listOfUsersGroupsAndServices: number[] = [];
    let temporaryListOfUserIds = [];
    let temporaryListOfUserNames = [];
    let listOfContactTypes = [];

    // vars to send back to component that called this method
    let recipientsFromQuery: MailRecipientDetailsDto[] = [];

    return new Observable<GetMailRecipientsUtilsResponse>((observer) =>
      async.waterfall(
        [
          (cb) => {
            recipientsService.fetchMailRecipientFullInfos(mailId).subscribe({
              next: (res) => {
                recipientsFromQuery = res;
                // Chaque destinataire va aller dans une liste en fct de son type => Libre , normal, service, groupe
                recipientsFromQuery.forEach((dest) => {
                  // destinataire libre
                  if (dest.type === 'DESTINATAIRE' && !dest.contactId && !dest.contactiAdminId) {
                    const recipient = new MailDestinataire(dest);
                    recipient.libre = dest.libre;
                    recipient.setName(dest.libre);
                    listOfFreeRecipients.push(recipient);
                  }
                  // destinataire basique
                  if (dest.type === 'DESTINATAIRE' && dest.libre === null) {
                    if (dest.contactId || dest.contactiAdminId) {
                      //ici on va juste récupérer la liste des destinataires "normaux"
                      recipientNotFreeList.push(dest);
                    }
                  } else if (dest.type === 'SERVICE' && dest.isService && dest.serviceId) {
                    const recipientService = servicesList.find((s) => s.value === dest.serviceId);
                    const destService = new MailDestinataire(dest);
                    if (recipientService) {
                      destService.setServiceName(recipientService.description);
                      destService.setServiceEmail(recipientService.email);
                    }
                    listOfServicesRecipients.push(destService);

                    // Here we add the contactiAdminId to the list of users groups and services 'cause it'll be used later to be displayed next to the service
                    // in fact it's the user who answered the action
                    // CDV-355
                    if (dest.contactiAdminId) {
                      listOfUsersGroupsAndServices.push(dest.contactiAdminId);
                    }
                  } else if (dest.type === 'GROUPE' && dest.isGroup && dest.groupeId) {
                    const recipientGroup = listOfGroups.find((g) => g.id === dest.groupeId);
                    const destGroup = new MailDestinataire(dest);
                    if (recipientGroup) {
                      destGroup.setGroupName(recipientGroup.description);
                    }
                    listOfGroupsRecipients.push(destGroup);

                    // Here we add the contactiAdminId to the list of users groups and services 'cause it'll be used later to be displayed next to the service
                    // in fact it's the user who answered the action
                    // CDV-355
                    if (dest.contactiAdminId) {
                      listOfUsersGroupsAndServices.push(dest.contactiAdminId);
                    }
                  }
                });
                cb(null);
              },
              error: (error) => {
                console.error(error);
                observer.next({ error: 'courrier.errorLoadingRecipients' });
                observer.complete();
              },
            });
          },
          (cb) => {
            //On va d'abord s'occuper des destinataires de iAdmin
            let listOfContactsIdsiAdmin = recipientNotFreeList
              .filter((recipient) => recipient.contactiAdminId)
              .map((recipient) => recipient.contactiAdminId);

            // Then we need to add the list of ids from listOfUsersGroupsAndServices to be able to fetch their informations
            if (listOfUsersGroupsAndServices?.length) {
              listOfContactsIdsiAdmin = listOfContactsIdsiAdmin.concat(listOfUsersGroupsAndServices);
            }
            listOfContactsIdsiAdmin = _.uniq(listOfContactsIdsiAdmin);

            if (listOfContactsIdsiAdmin.length) {
              contactsService.fetchContacts('iAdmin', { params: { Ids: listOfContactsIdsiAdmin.join(',') } }).subscribe({
                next: (values) => {
                  recipientNotFreeList.forEach((dest) => {
                    if (dest.contactiAdminId) {
                      const rightContact = values.find((contact) => contact.id === dest.contactiAdminId);
                      const destinataire = new MailDestinataire(dest);
                      destinataire.nom = rightContact.nom ? rightContact.nom : '';
                      destinataire.prenom = rightContact.prenom ? rightContact.prenom : '';
                      destinataire.setName(destinataire.nom + ' ' + destinataire.prenom);
                      destinataire.contactName = destinataire.name;
                      destinataire.setAddress(rightContact.adressePrincipale);
                      destinataire.user = rightContact.user;
                      listOfUsersRecipients.push(destinataire);
                    }
                  });

                  // After that, if we have ids in listOfUsersGroupsAndServices, we get those infos to add the user's name right after the service / group with a contactiAdminId
                  // CDV-355
                  if (listOfUsersGroupsAndServices?.length) {
                    listOfServicesRecipients.forEach((serviceRecipient) => {
                      if (serviceRecipient.contactiAdminId) {
                        const rightContact = values.find((contact) => contact.id === serviceRecipient.contactiAdminId);
                        serviceRecipient.serviceUserName =
                          (rightContact.nom ? rightContact.nom : '') + ' ' + (rightContact.prenom ? rightContact.prenom : '');
                      }
                    });
                    listOfGroupsRecipients.forEach((groupRecipient) => {
                      if (groupRecipient.contactiAdminId) {
                        const rightContact = values.find((contact) => contact.id === groupRecipient.contactiAdminId);
                        groupRecipient.groupUserName =
                          (rightContact.nom ? rightContact.nom : '') + ' ' + (rightContact.prenom ? rightContact.prenom : '');
                      }
                    });
                  }
                  cb();
                },
                error: (error) => {
                  console.error(error);
                  observer.next({ error: 'courrier.errorLoadingRecipients' });
                  observer.complete();
                },
              });
            } else {
              cb();
            }
          },
          (cb) => {
            // Ici on va s'occuper des destinataires iCourrier
            let listOfContactIdsiCourrier = recipientNotFreeList.filter((recipient) => recipient.contactId).map((recipient) => recipient.contactId);
            listOfContactIdsiCourrier = _.uniq(listOfContactIdsiCourrier);

            if (listOfContactIdsiCourrier.length) {
              contactsService.fetchContacts('iCourrier', { params: { Ids: listOfContactIdsiCourrier.join(',') } }).subscribe({
                next: (values) => {
                  recipientNotFreeList.forEach((dest) => {
                    if (dest.contactId) {
                      const rightContact = values.find((contact) => contact.id === dest.contactId);
                      const destinataire = new MailDestinataire(dest);
                      destinataire.nom = rightContact.nom ? rightContact.nom : '';
                      destinataire.prenom = rightContact.prenom ? rightContact.prenom : '';
                      destinataire.setName(destinataire.nom + ' ' + destinataire.prenom);
                      destinataire.contactName = destinataire.name;
                      destinataire.setAddress(rightContact.adressePrincipale);
                      destinataire.user = rightContact.user;
                      listOfUsersRecipients.push(destinataire);
                    }
                  });
                  cb();
                },
              });
            } else {
              cb();
            }
          },
          (cb) => {
            if (fetchAllInfos) {
              temporaryListOfUserIds = [
                ...listOfUsersRecipients.map((user) => user.transmissionUserId),
                ...listOfFreeRecipients.map((free) => free.transmissionUserId),
                ...listOfServicesRecipients.map((service) => service.transmissionUserId),
                ...listOfGroupsRecipients.map((group) => group.transmissionUserId),
              ].filter((value) => value);
              temporaryListOfUserIds = _.uniq(temporaryListOfUserIds);
              usersService.fetchUsersFromiAdmin({ UserIds: temporaryListOfUserIds.join(',') }).subscribe({
                next: (res: UserDto[]) => {
                  temporaryListOfUserNames = res;
                  cb();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                },
              });
              cb();
            } else cb();
          },
          (cb) => {
            if (fetchAllInfos) {
              contactsGlobalService.fetchMappedContactTypesToPrimeNGDropdowns().subscribe({
                next: (res) => {
                  listOfContactTypes = res;
                  listOfUsersRecipients.map((user) => {
                    if (user.transmissionUserId) {
                      temporaryListOfUserNames.map((name) => {
                        if (name.userId === user.transmissionUserId) {
                          user.transmissionUserName =
                            (name.contactForUser.prenom ? name.contactForUser.prenom : '') +
                            ' ' +
                            (name.contactForUser.nom ? name.contactForUser.nom : '');
                          user.transmissionUserAdress = name.contactForUser.adresse
                            ? (name.contactForUser.adresse?.impression_Adresse ? name.contactForUser.adresse?.impression_Adresse + ' ' : '') +
                              (name.contactForUser.adresse?.impression_CodePostal ? name.contactForUser.adresse?.impression_CodePostal + ' ' : '') +
                              (name.contactForUser.adresse?.impression_Localite ? name.contactForUser.adresse?.impression_Localite : '')
                            : '/';
                          user.transmissionUserType = listOfContactTypes.find((contact) => contact.id === name.contactForUser.typeId)?.typeContact;
                        }
                      });
                    }
                  });
                  listOfFreeRecipients.map((free) => {
                    if (free.transmissionUserId) {
                      temporaryListOfUserNames.map((name) => {
                        if (name.userId === free.transmissionUserId) {
                          free.transmissionUserName =
                            (name.contactForUser.prenom ? name.contactForUser.prenom : '') +
                            ' ' +
                            (name.contactForUser.nom ? name.contactForUser.nom : '');
                          free.transmissionUserAdress = name.contactForUser.adresse
                            ? (name.contactForUser.adresse?.impression_Adresse ? name.contactForUser.adresse?.impression_Adresse + ' ' : '') +
                              (name.contactForUser.adresse?.impression_CodePostal ? name.contactForUser.adresse?.impression_CodePostal + ' ' : '') +
                              (name.contactForUser.adresse?.impression_Localite ? name.contactForUser.adresse?.impression_Localite : '')
                            : '/';
                          free.transmissionUserType = listOfContactTypes.find((contact) => contact.id === name.contactForUser.typeId)?.typeContact;
                        }
                      });
                    }
                  });
                  listOfServicesRecipients.map((service) => {
                    if (service.transmissionUserId) {
                      temporaryListOfUserNames.map((name) => {
                        if (name.userId === service.transmissionUserId) {
                          service.transmissionUserName =
                            (name.contactForUser.prenom ? name.contactForUser.prenom : '') +
                            ' ' +
                            (name.contactForUser.nom ? name.contactForUser.nom : '');
                          service.transmissionUserAdress = name.contactForUser.adresse
                            ? (name.contactForUser.adresse?.impression_Adresse ? name.contactForUser.adresse?.impression_Adresse + ' ' : '') +
                              (name.contactForUser.adresse?.impression_CodePostal ? name.contactForUser.adresse?.impression_CodePostal + ' ' : '') +
                              (name.contactForUser.adresse?.impression_Localite ? name.contactForUser.adresse?.impression_Localite : '')
                            : '/';
                          service.transmissionUserType = listOfContactTypes.find((contact) => contact.id === name.contactForUser.typeId)?.typeContact;
                        }
                      });
                    }
                  });
                  listOfGroupsRecipients.map((group) => {
                    if (group.transmissionUserId) {
                      temporaryListOfUserNames.map((name) => {
                        if (name.userId === group.transmissionUserId) {
                          group.transmissionUserName =
                            (name.contactForUser.prenom ? name.contactForUser.prenom : '') +
                            ' ' +
                            (name.contactForUser.nom ? name.contactForUser.nom : '');
                          group.transmissionUserAdress = name.contactForUser.adresse
                            ? (name.contactForUser.adresse?.impression_Adresse ? name.contactForUser.adresse?.impression_Adresse + ' ' : '') +
                              (name.contactForUser.adresse?.impression_CodePostal ? name.contactForUser.adresse?.impression_CodePostal + ' ' : '') +
                              (name.contactForUser.adresse?.impression_Localite ? name.contactForUser.adresse?.impression_Localite : '')
                            : '/';
                          group.transmissionUserType = listOfContactTypes.find((contact) => contact.id === name.contactForUser.typeId)?.typeContact;
                        }
                      });
                    }
                  });
                  cb();
                },
              });
            } else cb();
          },
        ],
        () => {
          const response: GetMailRecipientsUtilsResponse = {
            recipientsFromQuery: recipientsFromQuery,
            userList: listOfUsersRecipients,
            freeList: listOfFreeRecipients,
            serviceList: listOfServicesRecipients,
            groupList: listOfGroupsRecipients,
          };
          observer.next(response);
          observer.complete();
        }
      )
    );
  }
}
